import type { ActionSummary } from '../../../core/api/types';

/** The only colour in the application means time pressure (research R-007). */
export type Urgency = 'overdue' | 'now' | 'soon' | 'later';

export interface Urgent {
  readonly band: Urgency;
  readonly text: string;
}

/**
 * What the date column actually shows — four different things, not a due date with empty cells
 * (the reference's `TodosHelper.DateSpan`; research R-004).
 */
export type DateCell =
  | { readonly kind: 'completed'; readonly at: string }
  /**
   * `blockers` is what the caller could NAME; `count` is how many there are. They differ whenever the
   * caller's list does not contain its own rows' blockers — the Tickler (a deferred action blocked by
   * an available one), `/done` (archived blocked by open), `/tags/:name` (tagged blocked by
   * untagged). Before issue 134 only `blockers` existed, so those rows rendered a tooltip reading
   * "Waiting on: " and stopping.
   */
  | { readonly kind: 'blocked'; readonly blockers: readonly string[]; readonly count: number }
  | { readonly kind: 'deferred'; readonly returnsAt: string; readonly dueAt: string | null }
  | { readonly kind: 'due'; readonly at: string; readonly band: Urgency; readonly text: string }
  /**
   * issue 133 — the action comes back AFTER it was due. Only ever produced for a caller whose own listing
   * states the return date (the Tickler), because only there is "when does it come back" the
   * question. `at` is the due date and `returnsAt` the deferral, so a template can show either.
   */
  | {
      readonly kind: 'returns-late'; readonly at: string; readonly returnsAt: string;
      readonly band: Urgency; readonly text: string;
    }
  | { readonly kind: 'none' };

/**
 * Whole days between a **day-valued** timestamp and the practitioner's today.
 *
 * Due and deferral dates are days, not instants: quick-add's `<260901` is stored as midnight UTC and
 * means "1 September" to everyone, so the date's **UTC** calendar day is the one that counts.
 *
 * `today` arrives **already reduced** — midnight UTC of the practitioner's calendar day, from
 * `core/time/calendar-day.ts` (010 FR-006). This function used to reduce it here, through the
 * browser's local getters, and that was one of four independent reductions the application had; it
 * also made "today" the *browser's* today rather than the practitioner's, which is half of the
 * defect 010 exists to fix. Both operands are now midnight UTC, so the subtraction is exact whole
 * days across a DST boundary as well.
 */
function daysFromToday(iso: string, today: Date): number {
  const then = new Date(iso);
  const date = Date.UTC(then.getUTCFullYear(), then.getUTCMonth(), then.getUTCDate());
  return Math.round((date - today.getTime()) / 86_400_000);
}

/**
 * The reference's ramp exactly (DateLabelHelper :104-118, :15-23, :253-268):
 * overdue → red, today/tomorrow → amber, within a week → orange, later → green.
 * PRD §3.2 says today is red; it is wrong, and 006 FR-006 overrules it.
 */
export function urgencyFor(dueAt: string, today: Date): Urgent {
  const days = daysFromToday(dueAt, today);

  if (days <= -1) {
    const overdueBy = Math.abs(days);
    return { band: 'overdue', text: `Overdue by ${overdueBy} ${overdueBy === 1 ? 'day' : 'days'}` };
  }

  if (days === 0) return { band: 'now', text: 'Due today' };
  if (days === 1) return { band: 'now', text: 'Due tomorrow' };
  if (days <= 7) return { band: 'soon', text: `Due in ${days} days` };
  return { band: 'later', text: `Due in ${days} days` };
}

/**
 * @param returnDateInHeading 031 FR-010. The caller's own listing already states this action's return
 * date — the Tickler groups by it — so the `deferred` arm is skipped and the row falls through to
 * whatever it would otherwise have said. **Exactly one arm is removed**, not the whole cell: a
 * blocked-and-deferred row still names its blockers, and a row with a due date still shows its urgency
 * band. 031 intended that to make the Tickler the one place an overshoot is visible; it did not —
 * `urgencyFor` bands against TODAY, so an action returning twelve days late rendered green. issue 133 adds
 * the `returns-late` arm, gated on this same flag rather than on a second one.
 *
 * Optional and defaulting to today's behaviour. **The trade this optionality makes, and the check that
 * compensates for it, are argued once — on `ActionRowLookups.returnDateInHeading`**, where the field
 * lives. Stating them here as well had already produced two copies that disagreed: one named
 * `readOnly` as the precedent and the other the asymmetry with `stalenessStarts`, and one of them
 * miscounted the screens.
 */
export function dateCellFor(
  action: ActionSummary,
  today: Date,
  blockerDescriptions: readonly string[],
  returnDateInHeading = false,
): DateCell {
  if (action.completedAt) return { kind: 'completed', at: action.completedAt };
  if (action.blockedBy.length > 0) {
    return { kind: 'blocked', blockers: blockerDescriptions, count: action.blockedBy.length };
  }

  // A deferral date already past is not a deferral — the action is available (002's derived rule).
  if (!returnDateInHeading && action.deferUntil && daysFromToday(action.deferUntil, today) > 0) {
    return { kind: 'deferred', returnsAt: action.deferUntil, dueAt: action.dueAt ?? null };
  }

  // issue 133. Reached only when the caller states the return date in its own heading, which is the
  // Tickler alone — so this arm is invisible to the other five screens by construction rather than
  // by a second optional flag, which is the smell the issue warned about. Guarded on the deferral
  // still being in the future, mirroring the `deferred` arm above: a deferral already past is not a
  // deferral, and an action that has already come back is not returning late.
  if (returnDateInHeading && action.dueAt && action.deferUntil) {
    const returnsIn = daysFromToday(action.deferUntil, today);
    const lateBy = returnsIn - daysFromToday(action.dueAt, today);
    if (returnsIn > 0 && lateBy > 0) {
      return {
        kind: 'returns-late',
        at: action.dueAt,
        returnsAt: action.deferUntil,
        // Red, and that is consistent with the palette rule rather than an exception to it: colour
        // means time pressure, and a deferral that overshoots a deadline is the sharpest time
        // pressure this screen can show. Words as well, because the band alone cannot say by how
        // much — and the number is the whole finding.
        band: 'overdue',
        text: `Returns ${lateBy} ${lateBy === 1 ? 'day' : 'days'} after it was due`,
      };
    }
  }

  if (action.dueAt) return { kind: 'due', at: action.dueAt, ...urgencyFor(action.dueAt, today) };
  return { kind: 'none' };
}
